import {Helmet} from '@dr.pogodin/react-helmet';
import {
  BreadcrumbType,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, HStack, Space} from 'platform/foundation';
import {z} from 'zod';

import {useParams} from 'react-router-dom';

import {
  CreateOfferSettingRequestBody,
  CreateOfferSettingRequestBodySchema,
  useCreateOfferSettingMutation,
  useGetOfferSettingQuery,
  useUpdateOfferSettingMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {ALL_BRANCHES_VALUE, handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

const allBranchesOption = [
  {value: ALL_BRANCHES_VALUE, label: i18n.t('page.offerValidity.labels.allBranches')},
];

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('page.businessCases.title'),
    href: settingsRoutes.businessCase,
  },
  {
    href: settingsRoutes.businessCaseOfferValidity,
    label: i18n.t('page.settings.labels.offerValidity'),
  },
];

type FormValues = CreateOfferSettingRequestBody;

const MIN_VALIDITY_DAYS = 1;

const formSchema = CreateOfferSettingRequestBodySchema.extend({
  validityDays: z
    .number()
    .int()
    .min(
      MIN_VALIDITY_DAYS,
      i18n.t('general.errors.number.greaterOrEqual', {min: MIN_VALIDITY_DAYS})
    )
    .nullable(),
});

export function BusinessCaseOfferValidityDetail() {
  const navigate = useNavigate();
  const {id} = useParams();
  const isCreating = id === undefined || id === '';

  const {branchOptions, data: branches} = useBranches();

  const {data, isLoading, isError} = useGetOfferSettingQuery(
    {settingId: id ?? ''},
    {skip: isCreating}
  );
  const [createOfferSetting] = useCreateOfferSettingMutation();
  const [updateOfferSetting] = useUpdateOfferSettingMutation();

  const title = isCreating
    ? i18n.t('page.offerValidity.labels.new')
    : (branches?.branchListItems.find((branch) => branch.id === data?.branchId)?.marketingName ??
      i18n.t('page.offerValidity.labels.edit'));

  const branchOptionsWithAll = [...allBranchesOption, ...branchOptions];

  const handleSubmit: FormSubmitHandler<FormValues> = (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const payload = {
      branchId: values.branchId === ALL_BRANCHES_VALUE ? null : values.branchId,
      validityEnabled: values.validityEnabled,
      validityDays: values.validityDays,
    };

    const submitAction = isCreating
      ? createOfferSetting({
          createOfferSettingRequestBody: payload,
        })
      : updateOfferSetting({settingId: id, updateOfferSettingRequestBody: payload});

    const successKey = isCreating
      ? 'entity.offerValidity.notifications.successfullyCreated'
      : 'general.notifications.changesSuccessfullySaved';

    return submitAction
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t(successKey));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.businessCaseOfferValidity);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: Partial<FormValues> = {
    branchId: data?.branchId ?? ALL_BRANCHES_VALUE,
    validityDays: data?.validityDays ?? MIN_VALIDITY_DAYS,
    validityEnabled: data?.validityEnabled ?? true,
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs,
        }}
        data-testid={testIds.settings.businessCaseOfferValidityDetail('settingsTemplate')}
        isLoading={isLoading}
        isError={isError}
      >
        <Form<FormValues>
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          experimentalZodSchema={formSchema}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <SettingsSection>
                <FormField
                  control={control}
                  type="switch"
                  name="validityEnabled"
                  label={i18n.t('page.offerValidity.labels.validityEnabled')}
                  isRequired
                />

                <Space vertical={4} />

                <HStack width="100%" spacing={4}>
                  <Box flex={1} width="100%">
                    <FormField
                      control={control}
                      type="choice"
                      name="branchId"
                      data-testid={testIds.settings.businessCaseOfferValidityDetail('branch')}
                      options={branchOptionsWithAll}
                      label={i18n.t('page.offerValidity.labels.branch')}
                      isRequired
                      isNotClearable
                    />
                  </Box>
                  <Box flex={1} width="100%">
                    <FormField
                      control={control}
                      type="number"
                      isStepperVisible
                      minStepperValue={MIN_VALIDITY_DAYS}
                      name="validityDays"
                      label={i18n.t('page.offerValidity.labels.numberOfDays')}
                      data-testid={testIds.settings.businessCaseOfferValidityDetail('validityDays')}
                      isRequired
                    />
                  </Box>
                </HStack>
              </SettingsSection>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: isCreating
                      ? i18n.t('general.actions.discard')
                      : i18n.t('general.actions.discardChanges'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.businessCaseOfferValidity),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: isCreating
                      ? i18n.t('general.actions.save')
                      : i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
